import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useAdminCategories } from '@/presentation/hook/useAdminCategories';
import { Btn, Alert, Modal, InputField, MultilineText } from '@/presentation/component/ui';
import { inputClass } from '@/presentation/component/formStyles';
import { truncate } from '@/presentation/util/text';
import type { Category } from '@/domain/model/Category';

interface CategoryFormData {
  name: string;
  description: string;
  displayOrder: number;
}

const EMPTY_FORM: CategoryFormData = { name: '', description: '', displayOrder: 0 };

export const AdminCategoriesPage = () => {
  const { t } = useTranslation();
  const {
    categories,
    isLoading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    clearError,
  } = useAdminCategories();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>(EMPTY_FORM);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleRowClick = (categoryId: string) => {
    navigate(`/admin/categories/${categoryId}/subcategories`);
  };

  const openCreateForm = () => {
    setEditingCategory(null);
    setFormData(EMPTY_FORM);
    setIsFormOpen(true);
  };

  const openEditForm = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      displayOrder: category.displayOrder,
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCategory) {
      await updateCategory(editingCategory.id, formData);
    } else {
      await createCategory(formData);
    }
    setIsFormOpen(false);
    setEditingCategory(null);
    setFormData(EMPTY_FORM);
  };

  const handleConfirmDelete = async () => {
    if (deletingId) {
      const success = await deleteCategory(deletingId);
      if (success) {
        toast.success(t('admin.categoryDeleted'));
        fetchCategories();
      } else {
        toast.error(t('admin.deleteCategoryConflict'));
      }
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-ink)]">{t('admin.categories')}</h1>
          <p className="text-sm text-[var(--color-ink-faint)]">
            {categories.length} {t('admin.categories').toLowerCase()}
          </p>
        </div>
        <Btn kind="primary" icon={<Plus className="h-4 w-4" />} onClick={openCreateForm}>
          {t('admin.addCategory')}
        </Btn>
      </div>

      {error && (
        <Alert kind="bad" onClose={clearError} className="mt-4">
          {t(error)}
        </Alert>
      )}

      {isLoading && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">{t('common.loading')}</p>
      )}

      {!isLoading && categories.length === 0 && !error && (
        <p className="mt-6 text-center text-[var(--color-ink-faint)]">{t('admin.noCategories')}</p>
      )}

      {categories.length > 0 && (
        <>
          {/* Desktop table */}
          <div className="mt-6 hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line)] text-[var(--color-ink-faint)]">
                  <th className="pb-3 pr-4 font-medium">{t('admin.categoryName')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.categoryDescription')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.subcategories')}</th>
                  <th className="pb-3 pr-4 font-medium">{t('admin.displayOrder')}</th>
                  <th className="pb-3 font-medium">{t('admin.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr
                    key={cat.id}
                    role="button"
                    tabIndex={0}
                    aria-label={t('admin.viewSubcategories')}
                    className="cursor-pointer border-b border-[var(--color-line)] text-[var(--color-ink)] transition-colors hover:bg-[var(--color-surface-2)]"
                    onClick={() => handleRowClick(cat.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick(cat.id);
                      }
                    }}
                  >
                    <td className="py-3 pr-4 font-medium">{cat.name}</td>
                    <td className="py-3 pr-4">
                      <MultilineText className="text-[var(--color-ink-soft)]">
                        {truncate(cat.description, 60)}
                      </MultilineText>
                    </td>
                    <td className="py-3 pr-4">{cat.subcategories?.length ?? 0}</td>
                    <td className="py-3 pr-4">{cat.displayOrder}</td>
                    <td className="py-3">
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Btn
                          size="sm"
                          kind="ghost"
                          aria-label={t('admin.editCategory')}
                          onClick={() => openEditForm(cat)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Btn>
                        <Btn
                          size="sm"
                          kind="ghost"
                          aria-label={t('admin.deleteCategory')}
                          onClick={() => setDeletingId(cat.id)}
                        >
                          <Trash2 className="h-4 w-4 text-[var(--color-bad)]" />
                        </Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mt-6 space-y-3 md:hidden">
            {categories.map((cat) => (
              <div
                key={cat.id}
                role="button"
                tabIndex={0}
                aria-label={t('admin.viewSubcategories')}
                className="cursor-pointer rounded-xl border border-[var(--color-line)] bg-[var(--color-surface)] p-4 transition-colors hover:bg-[var(--color-surface-2)]"
                onClick={() => handleRowClick(cat.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(cat.id);
                  }
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[var(--color-ink)]">{cat.name}</p>
                    <MultilineText className="mt-1 text-sm text-[var(--color-ink-soft)]">
                      {cat.description}
                    </MultilineText>
                  </div>
                  <div className="ml-2 flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Btn
                      size="sm"
                      kind="ghost"
                      aria-label={t('admin.editCategory')}
                      onClick={() => openEditForm(cat)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Btn>
                    <Btn
                      size="sm"
                      kind="ghost"
                      aria-label={t('admin.deleteCategory')}
                      onClick={() => setDeletingId(cat.id)}
                    >
                      <Trash2 className="h-4 w-4 text-[var(--color-bad)]" />
                    </Btn>
                  </div>
                </div>
                <div className="mt-2 flex gap-4 text-xs text-[var(--color-ink-faint)]">
                  <span>
                    {cat.subcategories?.length ?? 0} {t('admin.subcategories')}
                  </span>
                  <span>
                    {t('admin.displayOrder')}: {cat.displayOrder}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create / Edit modal */}
      <Modal
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={editingCategory ? t('admin.editCategory') : t('admin.addCategory')}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <InputField
            label={t('admin.categoryName')}
            placeholder={t('admin.categoryNamePlaceholder')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="category-description"
              className="text-sm font-medium text-[var(--color-ink-soft)]"
            >
              {t('admin.categoryDescription')}
            </label>
            <textarea
              id="category-description"
              className={inputClass()}
              placeholder={t('admin.categoryDescriptionPlaceholder')}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              required
            />
          </div>
          <InputField
            label={t('admin.displayOrder')}
            type="number"
            min={0}
            value={formData.displayOrder}
            onChange={(e) => setFormData({ ...formData, displayOrder: Number(e.target.value) })}
            required
          />
          <div className="flex justify-end gap-2 pt-2">
            <Btn kind="ghost" type="button" onClick={() => setIsFormOpen(false)}>
              {t('admin.cancel')}
            </Btn>
            <Btn kind="primary" type="submit" disabled={isLoading} isLoading={isLoading}>
              {t('admin.save')}
            </Btn>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        open={deletingId !== null}
        onClose={() => setDeletingId(null)}
        title={t('admin.confirmDelete')}
        size="sm"
      >
        <p className="text-sm text-[var(--color-ink-soft)]">{t('admin.confirmDeleteCategory')}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Btn kind="ghost" onClick={() => setDeletingId(null)}>
            {t('admin.cancel')}
          </Btn>
          <Btn
            kind="primary"
            onClick={handleConfirmDelete}
            disabled={isLoading}
            isLoading={isLoading}
          >
            {t('admin.deleteCategory')}
          </Btn>
        </div>
      </Modal>
    </div>
  );
};
